import { userCacheKey } from '../quiz-generation/utils/paperProgressCache';

/**
 * Where a sitting's progress lives. There is no sitting table (the client holds
 * its run ids; each run is independently resumable via GET /runs/<id>), so this
 * is the one place that state is kept - one entry per (user, paper, session), on
 * this device. Shared by the sitting screen, which writes it, and the brief, which
 * reads it to offer "Resume" rather than silently resuming behind a "Start"
 * button. Kept under the signed-in user's id (userCacheKey), so a shared school
 * computer never shows one student's sitting to the next: no user id, nothing is
 * read or written. Logout clears it for every user (interviewProgressStorage).
 */
export const SITTING_PREFIX = 'ivori-interview-sitting:';

/** A live interview is one live set; its progress is kept in a slot of its own. */
export const liveSlot = (leafKey, setId) => [`live-${leafKey}`, setId || 'live'];

/** A station the live interview lists but cannot run (a group or practical
 * task) takes its place in questionIds under this marker, so the order,
 * the progress pips and the resume index all stay the school's own. */
export const NOTICE_PREFIX = 'notice:';
export const isNoticeId = (qid) => typeof qid === 'string' && qid.startsWith(NOTICE_PREFIX);

/** The user's key for one (paper, session), or null when there is no user. */
function storageKey(userId, paperId, sessionId) {
  return userCacheKey(`${SITTING_PREFIX}${paperId}:${sessionId}`, userId);
}

export function loadSittingState(userId, paperId, sessionId) {
  const key = storageKey(userId, paperId, sessionId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // corrupted/blocked storage degrades to "start fresh", never a crash
  }
}

export function saveSittingState(userId, paperId, sessionId, state) {
  const key = storageKey(userId, paperId, sessionId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* private-window/quota - the sitting still works, it just won't resume a reload */
  }
}

export function clearSittingState(userId, paperId, sessionId) {
  const key = storageKey(userId, paperId, sessionId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch { /* nothing to clean up if this failed */ }
}

/** 'none' | 'in-progress' | 'finished' (finished, report not yet opened). */
export function sittingProgress(userId, paperId, sessionId) {
  const state = loadSittingState(userId, paperId, sessionId);
  if (!state || !Array.isArray(state.questionIds)) return 'none';
  return state.complete ? 'finished' : 'in-progress';
}

export function finishedRunIds(state) {
  return (state?.questionIds || [])
    .map((qid) => state.runByQuestion?.[qid])
    .filter(Boolean);
}
